// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Crew Goals Configuration
 * 
 * Defines goal templates for crew members to provide motivation and XP bonuses
 */

export const GOAL_TYPES = {
  PERSONAL: 'personal',
  PROFESSIONAL: 'professional', 
  RELATIONSHIP: 'relationship'
};

export const GOAL_STATUS = {
  ACTIVE: 'active',
  COMPLETED: 'completed',
  FAILED: 'failed'
};

// Personal goals - individual aspirations
export const PERSONAL_GOALS = [
  {
    id: 'explore_systems',
    name: 'Explorer',
    description: 'Visit 5 different star systems',
    type: GOAL_TYPES.PERSONAL,
    target: 5,
    xpReward: 100,
    moraleBonus: 5,
    icon: '🌟'
  },
  {
    id: 'survive_combat',
    name: 'Survivor',
    description: 'Survive 3 combat encounters',
    type: GOAL_TYPES.PERSONAL,
    target: 3,
    xpReward: 75,
    moraleBonus: 3,
    icon: '🛡️'
  },
  {
    id: 'earn_credits',
    name: 'Entrepreneur',
    description: 'Help earn 10,000 credits through trading',
    type: GOAL_TYPES.PERSONAL,
    target: 10000,
    xpReward: 150,
    moraleBonus: 8,
    icon: '💰'
  },
  {
    id: 'level_up',
    name: 'Self-Improvement',
    description: 'Reach next experience level',
    type: GOAL_TYPES.PERSONAL,
    target: 1,
    xpReward: 200,
    moraleBonus: 10,
    icon: '📈'
  }
];

// Professional goals - role-specific objectives
export const PROFESSIONAL_GOALS = [
  {
    id: 'pilot_efficiency',
    name: 'Fuel Master',
    description: 'Complete 10 jumps with optimal fuel efficiency',
    type: GOAL_TYPES.PROFESSIONAL,
    roles: ['pilot'],
    target: 10,
    xpReward: 125,
    moraleBonus: 6,
    icon: '⚡'
  },
  {
    id: 'engineer_repairs',
    name: 'Master Mechanic',
    description: 'Complete 15 successful repairs',
    type: GOAL_TYPES.PROFESSIONAL,
    roles: ['engineer'],
    target: 15,
    xpReward: 125,
    moraleBonus: 6,
    icon: '🔧'
  },
  {
    id: 'gunner_accuracy',
    name: 'Sharpshooter',
    description: 'Achieve 20 combat kills',
    type: GOAL_TYPES.PROFESSIONAL,
    roles: ['gunner'],
    target: 20,
    xpReward: 150,
    moraleBonus: 8,
    icon: '🎯'
  },
  {
    id: 'medic_healing',
    name: 'Life Saver',
    description: 'Heal 10 injured crew members',
    type: GOAL_TYPES.PROFESSIONAL,
    roles: ['medic'],
    target: 10,
    xpReward: 125,
    moraleBonus: 7,
    icon: '⚕️'
  },
  {
    id: 'scientist_scans',
    name: 'Data Collector',
    description: 'Complete 25 successful scans',
    type: GOAL_TYPES.PROFESSIONAL,
    roles: ['scientist'],
    target: 25,
    xpReward: 125,
    moraleBonus: 6,
    icon: '📡'
  }
];

// Relationship goals - social objectives
export const RELATIONSHIP_GOALS = [
  {
    id: 'make_friend',
    name: 'Friendship',
    description: 'Form a friendship with another crew member',
    type: GOAL_TYPES.RELATIONSHIP,
    target: 1,
    xpReward: 100,
    moraleBonus: 10,
    icon: '🤝'
  },
  {
    id: 'mentor_rookie',
    name: 'Mentor',
    description: 'Help train a rookie crew member',
    type: GOAL_TYPES.RELATIONSHIP,
    target: 1,
    xpReward: 150,
    moraleBonus: 8,
    icon: '👨‍🏫'
  },
  {
    id: 'team_player',
    name: 'Team Player',
    description: 'Participate in 5 group activities',
    type: GOAL_TYPES.RELATIONSHIP,
    target: 5,
    xpReward: 75,
    moraleBonus: 5,
    icon: '👥'
  }
];

export const ALL_GOALS = [
  ...PERSONAL_GOALS,
  ...PROFESSIONAL_GOALS,
  ...RELATIONSHIP_GOALS
];

/**
 * Get available goals for a crew member based on their role and current status
 */
export function getAvailableGoals(crewMember, existingGoals = []) {
  const existingGoalIds = existingGoals.map(g => g.goalId);
  
  return ALL_GOALS.filter(goal => {
    // Skip if already has this goal
    if (existingGoalIds.includes(goal.id)) return false;
    
    // Check role requirements
    if (goal.roles && !goal.roles.includes(crewMember.role)) return false;
    
    // Skip level up goal if already at max level
    if (goal.id === 'level_up' && crewMember.experience === 'ELITE') return false;
    
    return true;
  });
}

/**
 * Generate random goals for a crew member
 */
export function generateRandomGoals(crewMember, count = 2) {
  const available = getAvailableGoals(crewMember);
  const selected = [];
  
  // Ensure at least one personal goal
  const personalGoals = available.filter(g => g.type === GOAL_TYPES.PERSONAL);
  if (personalGoals.length > 0) {
    selected.push(personalGoals[Math.floor(Math.random() * personalGoals.length)]);
  }
  
  // Fill remaining slots with any available goals
  const remaining = available.filter(g => !selected.includes(g));
  while (selected.length < count && remaining.length > 0) {
    const randomIndex = Math.floor(Math.random() * remaining.length);
    selected.push(remaining.splice(randomIndex, 1)[0]);
  }
  
  return selected.map(goal => ({
    goalId: goal.id,
    goalData: goal,
    status: GOAL_STATUS.ACTIVE,
    progress: 0,
    assignedAt: Date.now()
  }));
}